/**
 * 深拷贝对象
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item)) as T;
  }

  if (obj instanceof RegExp) {
    return new RegExp(obj.source, obj.flags) as T;
  }

  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }

  return obj;
}

/**
 * 深度合并对象
 */
// export function deepMerge<T extends Record<string, any>>(
//   target: T,
//   ...sources: Partial<T>[]
// ): T {
//   if (!sources.length) return target;
//   const source = sources.shift();

//   if (isObject(target) && isObject(source)) {
//     for (const key in source) {
//       if (isObject(source[key])) {
//         if (!target[key]) Object.assign(target, { [key]: {} });
//         deepMerge(target[key] as any, source[key] as any);
//       } else {
//         Object.assign(target, { [key]: source[key] });
//       }
//     }
//   }

//   return deepMerge(target, ...sources);
// }

export function deepMerge<T = any>(target: T, source: Partial<T>): T {
  // 如果源对象为空或不是对象，直接返回目标对象
  if (!source || typeof source !== 'object' || source === null) return target;

  // 如果目标对象为空或不是对象，直接返回源对象
  if (!target || typeof target !== 'object' || target === null)
    return source as T;

  // 创建目标对象的副本，避免修改原对象
  const result = Array.isArray(target) ? [...target] : { ...target };

  // 遍历源对象的所有属性
  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      const sourceValue = source[key];
      const targetValue = (result as any)[key];

      if (Array.isArray(sourceValue)) {
        // 如果源值是数组，直接使用源数组（可以根据需求调整策略）
        (result as any)[key] = [...sourceValue];
      } else if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        sourceValue.constructor === Object
      ) {
        // 如果源值是普通对象，递归合并
        (result as any)[key] = deepMerge(targetValue, sourceValue);
      } else {
        // 其他情况直接赋值
        (result as any)[key] = sourceValue;
      }
    }
  }

  return result as T;
}

/**
 * 检查是否为对象
 */
export function isObject(item: any): item is Record<string, any> {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * 检查对象是否为空
 */
export function isEmpty(obj: any): boolean {
  if (obj === null || obj === undefined) return true;
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
}

/**
 * 获取嵌套对象属性值
 */
export function get(
  obj: any,
  path: string | string[],
  defaultValue?: any
): any {
  const keys = Array.isArray(path) ? path : path.split('.');
  let result = obj;

  for (const key of keys) {
    if (result === null || result === undefined) {
      return defaultValue;
    }
    result = result[key];
  }

  return result !== undefined ? result : defaultValue;
}

/**
 * 设置嵌套对象属性值
 */
export function set(obj: any, path: string | string[], value: any): void {
  const keys = Array.isArray(path) ? path : path.split('.');
  const lastKey = keys.pop()!;
  let current = obj;

  for (const key of keys) {
    if (!(key in current) || !isObject(current[key])) {
      current[key] = {};
    }
    current = current[key];
  }

  current[lastKey] = value;
}

/**
 * 删除嵌套对象属性
 */
export function unset(obj: any, path: string | string[]): boolean {
  const keys = Array.isArray(path) ? path : path.split('.');
  const lastKey = keys.pop()!;
  let current = obj;

  for (const key of keys) {
    if (!(key in current) || !isObject(current[key])) {
      return false;
    }
    current = current[key];
  }

  if (lastKey in current) {
    delete current[lastKey];
    return true;
  }

  return false;
}

/**
 * 检查对象是否包含指定路径
 */
export function has(obj: any, path: string | string[]): boolean {
  const keys = Array.isArray(path) ? path : path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined || !(key in current)) {
      return false;
    }
    current = current[key];
  }

  return true;
}

/**
 * 获取对象所有键
 */
export function keys(obj: Record<string, any>): string[] {
  return Object.keys(obj);
}

/**
 * 获取对象所有值
 */
export function values<T>(obj: Record<string, T>): T[] {
  return Object.values(obj);
}

/**
 * 获取对象所有键值对
 */
export function entries<T>(obj: Record<string, T>): [string, T][] {
  return Object.entries(obj);
}

/**
 * 从键值对创建对象
 */
export function fromEntries<T>(entries: [string, T][]): Record<string, T> {
  return Object.fromEntries(entries);
}

/**
 * 选择对象的指定属性
 */
export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}

/**
 * 排除对象的指定属性
 */
export function omit<T, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
  const result = { ...obj };
  keys.forEach(key => {
    delete result[key];
  });
  return result;
}

/**
 * 对象属性映射
 */
export function mapKeys<T>(
  obj: Record<string, T>,
  mapper: (key: string, value: T) => string
): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [key, value] of entries(obj)) {
    const newKey = mapper(key, value);
    result[newKey] = value;
  }
  return result;
}

/**
 * 对象值映射
 */
export function mapValues<T, U>(
  obj: Record<string, T>,
  mapper: (value: T, key: string) => U
): Record<string, U> {
  const result: Record<string, U> = {};
  for (const [key, value] of entries(obj)) {
    result[key] = mapper(value, key);
  }
  return result;
}

/**
 * 过滤对象属性
 */
export function pickBy<T>(
  obj: Record<string, T>,
  predicate: (value: T, key: string) => boolean
): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [key, value] of entries(obj)) {
    if (predicate(value, key)) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * 反转对象键值
 */
export function invert(
  obj: Record<string, string | number>
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of entries(obj)) {
    result[String(value)] = key;
  }
  return result;
}

/**
 * 扁平化对象
 */
export function flatten(
  obj: Record<string, any>,
  prefix: string = '',
  separator: string = '.'
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of entries(obj)) {
    const newKey = prefix ? `${prefix}${separator}${key}` : key;

    if (isObject(value) && !Array.isArray(value)) {
      Object.assign(result, flatten(value, newKey, separator));
    } else {
      result[newKey] = value;
    }
  }

  return result;
}

/**
 * 反扁平化对象
 */
export function unflatten(
  obj: Record<string, any>,
  separator: string = '.'
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of entries(obj)) {
    set(result, key.split(separator), value);
  }

  return result;
}

/**
 * 深度比较两个对象是否相等
 */
export function isEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true;

  if (obj1 === null || obj2 === null) return false;
  if (obj1 === undefined || obj2 === undefined) return false;

  if (typeof obj1 !== typeof obj2) return false;

  if (typeof obj1 !== 'object') return obj1 === obj2;

  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!isEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!isEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

/**
 * 获取对象的深度
 */
export function getDepth(obj: any): number {
  if (!isObject(obj)) return 0;

  let maxDepth = 0;
  for (const value of values(obj)) {
    if (isObject(value)) {
      maxDepth = Math.max(maxDepth, getDepth(value));
    }
  }

  return maxDepth + 1;
}

/**
 * 冻结对象（深度冻结）
 */
export function deepFreeze<T>(obj: T): T {
  Object.freeze(obj);

  Object.getOwnPropertyNames(obj).forEach(prop => {
    const value = (obj as any)[prop];
    if (value && typeof value === 'object') {
      deepFreeze(value);
    }
  });

  return obj;
}
